/**
 * UpdateBannerProvider.tsx -- Polls /api/build-id and renders the existing
 * UpdateBanner when the deployed build differs from the current one.
 *
 * FR-013 (specs/prd.md): ambient update awareness via build-id polling.
 * Spec: specs/integration-plan.md Section 6.2.
 *
 * Exposes useUpdateBanner(): { available, refresh, dismiss } via React context.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { UpdateBanner } from "../layout/UpdateBanner";

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

interface UpdateBannerContextValue {
  /** True when the polled build id differs from VITE_BUILD_ID. */
  available: boolean;
  /** Triggers a full page reload. */
  refresh: () => void;
  /** Hides the banner for the remainder of this page lifecycle. */
  dismiss: () => void;
}

const UpdateBannerContext = createContext<UpdateBannerContextValue>({
  available: false,
  refresh: () => undefined,
  dismiss: () => undefined,
});

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Returns the update-banner context: { available, refresh, dismiss }.
 *
 * Must be called from a descendant of <UpdateBannerProvider>.
 */
export function useUpdateBanner(): UpdateBannerContextValue {
  return useContext(UpdateBannerContext);
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface UpdateBannerProviderProps {
  children: ReactNode;
  /** Override the default 60 000 ms poll interval (useful for tests). */
  pollIntervalMs?: number;
  /** Override the default endpoint (useful for tests). */
  buildIdEndpoint?: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const DEFAULT_POLL_INTERVAL_MS = 60_000;
const DEFAULT_BUILD_ID_ENDPOINT = "/api/build-id";

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function UpdateBannerProvider({
  children,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  buildIdEndpoint = DEFAULT_BUILD_ID_ENDPOINT,
}: UpdateBannerProviderProps) {
  const currentBuildId = import.meta.env.VITE_BUILD_ID as string | undefined;

  const [latestBuildId, setLatestBuildId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  /* Ref to track whether the component is still mounted, used by the
     async fetch callback to avoid state updates after unmount.        */
  const mountedRef = useRef(true);

  /* ---- Polling --------------------------------------------------- */

  useEffect(() => {
    mountedRef.current = true;

    const controller = new AbortController();

    async function poll() {
      try {
        const res = await fetch(buildIdEndpoint, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (mountedRef.current && data.buildId) {
          setLatestBuildId(data.buildId);
        }
      } catch {
        /* Network errors, aborts -- silently ignore. */
      }
    }

    const id = setInterval(poll, pollIntervalMs);

    return () => {
      mountedRef.current = false;
      controller.abort();
      clearInterval(id);
    };
  }, [pollIntervalMs, buildIdEndpoint]);

  /* ---- Derived state --------------------------------------------- */

  const available =
    latestBuildId !== null &&
    latestBuildId !== "" &&
    currentBuildId !== undefined &&
    latestBuildId !== currentBuildId;

  /* ---- Callbacks ------------------------------------------------- */

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  /* ---- Context value --------------------------------------------- */

  const ctxValue: UpdateBannerContextValue = {
    available,
    refresh,
    dismiss,
  };

  /* ---- Render ---------------------------------------------------- */

  return (
    <UpdateBannerContext.Provider value={ctxValue}>
      {available && !dismissed && (
        <UpdateBanner
          currentBuildId={currentBuildId ?? ""}
          latestBuildId={latestBuildId}
        />
      )}
      {children}
    </UpdateBannerContext.Provider>
  );
}

export default UpdateBannerProvider;
